import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getCsrfToken } from '../utils/auth';
import { KnowledgeIcon } from '../components/BrandIcons';
import '../styles/TraceBuilder.css';

const jsonHeaders = () => ({ 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() });
const BATCH_CAP = 100;

// The Trace Builder workspace (design §4). A step rail across the top; at the selected
// step, two columns — source items on the left, generated target drafts on the right.
// Nothing is written to Orcanos until a draft is approved (reuses /add-item).
export default function TraceBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setupId } = useParams();
  // Opened from the Traceability Dashboard's Studio button (v4.27.0): land on the first step
  // (level 1 → level 2) and tick the level-1 items it says are not or only partly covered.
  // Applied once, to the first source page that loads; the user can change it freely after.
  const preselectRef = useRef(Array.isArray(location.state?.preselect) ? location.state.preselect : null);
  const backTo = location.state?.from || `/trace/matrix/${setupId}`;

  const [meta, setMeta] = useState(null);        // {panel_name, product, base, steps}
  const [sel, setSel] = useState({ type: 'urs' }); // {type:'urs'} | {type:'step', idx}
  const [loadingSteps, setLoadingSteps] = useState(true);
  const [error, setError] = useState(null);

  // URS bootstrap (step 0)
  const [ursCount, setUrsCount] = useState(40);
  const [ursTitles, setUrsTitles] = useState([]);   // [{id, title, section, checked}]
  const [outlining, setOutlining] = useState(false);
  const [outlined, setOutlined] = useState(false);

  // Source column
  const [srcFilter, setSrcFilter] = useState('uncovered');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sources, setSources] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [sourceTotal, setSourceTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingSrc, setLoadingSrc] = useState(false);

  // Generation job + drafts
  const [job, setJob] = useState(null);          // {job_id, status, progress}
  const [groups, setGroups] = useState([]);       // [{source_key, source_name, drafts:[...], error}]
  const pollRef = useRef(null);

  // ---- Link-existing mode (v3.5.0) -------------------------------------------------
  // 'create' = draft NEW items (unchanged). 'link' = connect to items that already exist.
  const [mode, setMode] = useState(() => localStorage.getItem(`tbMode:${setupId}`) || 'create');
  const [targets, setTargets] = useState([]);
  const [selTargets, setSelTargets] = useState(new Set());
  const [tgtFilter, setTgtFilter] = useState('unlinked');
  const [tgtSearchInput, setTgtSearchInput] = useState('');
  const [tgtSearch, setTgtSearch] = useState('');
  const [tgtTotal, setTgtTotal] = useState(0);
  const [tgtNextOffset, setTgtNextOffset] = useState(0);
  const [tgtHasMore, setTgtHasMore] = useState(false);
  const [loadingTgt, setLoadingTgt] = useState(false);
  const [linkJob, setLinkJob] = useState(null);   // {job_id, status, progress}
  const [linkGroups, setLinkGroups] = useState([]); // [{source_key, source_name, suggestions:[…]}]
  const [linkMeta, setLinkMeta] = useState(null);   // pool size / truncation / ai_used
  const [confirm, setConfirm] = useState(null);     // {srcKeys, tgtKeys}
  const [linking, setLinking] = useState(false);
  const linkPollRef = useRef(null);

  useEffect(() => { localStorage.setItem(`tbMode:${setupId}`, mode); }, [mode, setupId]);

  const isUrs = sel.type === 'urs';
  const step = sel.type === 'step' ? (meta?.steps?.[sel.idx] || null) : null;

  const [engine, setEngine] = useState(null);   // {provider, model} shown in the footer

  // --------------------------------------------------------- load steps
  useEffect(() => {
    let alive = true;
    fetch(`/api/build/${setupId}/steps`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error('Could not open the builder for this panel'); return r.json(); })
      .then(d => {
        if (!alive) return;
        setMeta(d);
        setLoadingSteps(false);
        if (preselectRef.current) {
          const idx = (d.steps || []).findIndex(st => st.parent_block_id === d.base?.block_id);
          if (idx >= 0) setSel({ type: 'step', idx });
          else preselectRef.current = null;
        }
      })
      .catch(e => { if (alive) { setError(e.message); setLoadingSteps(false); } });
    return () => { alive = false; };
  }, [setupId]);

  // Which AI engine this account runs on (footer).
  useEffect(() => {
    let alive = true;
    fetch('/api/build/engine', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setEngine(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // --------------------------------------------------------- load sources
  const loadSources = useCallback(async (reset) => {
    if (!step) return;
    setLoadingSrc(true);
    try {
      const offset = reset ? 0 : nextOffset;
      const qs = new URLSearchParams({
        block_id: step.block_id, filter: srcFilter, search, offset: String(offset), limit: '50',
      });
      const res = await fetch(`/api/build/${setupId}/sources?${qs}`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not load source items');
      setSources(prev => reset ? d.items : [...prev, ...d.items]);
      if (reset && preselectRef.current) {
        const want = new Set(preselectRef.current);
        preselectRef.current = null;
        setSelected(new Set((d.items || []).filter(x => want.has(x.key)).map(x => x.key)));
      }
      setSourceTotal(d.source_total || 0);
      setNextOffset(d.next_offset || 0);
      setHasMore(!!d.has_more);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingSrc(false);
    }
  }, [step, srcFilter, search, nextOffset, setupId]);

  // Which target items the sources on the left already trace to. Selection-relative by
  // design: with nothing ticked we use every loaded source, so the list still means
  // something before the user has chosen. This is link state WITHIN THIS STEP — a target
  // traced from a requirement outside the loaded set reads as free (design §5.3).
  const linkedKeys = useCallback(() => {
    const pool = selected.size ? sources.filter(s => selected.has(s.key)) : sources;
    const out = new Set();
    pool.forEach(s => (s.traced_keys || []).forEach(k => out.add(k)));
    return [...out];
  }, [sources, selected]);

  const loadTargets = useCallback(async (reset) => {
    if (!step) return;
    setLoadingTgt(true);
    try {
      const res = await fetch(`/api/build/${setupId}/targets`, {
        method: 'POST', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify({
          block_id: step.block_id, filter: tgtFilter, search: tgtSearch,
          offset: reset ? 0 : tgtNextOffset, limit: 50, linked_keys: linkedKeys(),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not load existing items');
      setTargets(prev => reset ? d.items : [...prev, ...d.items]);
      setTgtTotal(d.target_total || 0);
      setTgtNextOffset(d.next_offset || 0);
      setTgtHasMore(!!d.has_more);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingTgt(false);
    }
  }, [step, tgtFilter, tgtSearch, tgtNextOffset, setupId, linkedKeys]);

  // Reset drafts/job whenever the selected step (or URS mode) changes.
  useEffect(() => {
    setGroups([]);
    setJob(null);
    setLinkGroups([]);
    setLinkJob(null);
    setLinkMeta(null);
    setSelTargets(new Set());
    if (step) { setSelected(new Set()); loadSources(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.type, sel.idx]);

  // Load the right column when link mode is showing the browse list.
  useEffect(() => {
    if (mode !== 'link' || !step || linkJob || linkGroups.length) return;
    setSelTargets(new Set());
    loadTargets(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, step?.block_id, tgtFilter, tgtSearch]);

  // Reload sources when the filter/search changes (step mode only).
  useEffect(() => {
    if (!step) return;
    setSelected(new Set());
    loadSources(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcFilter, search, step?.block_id]);

  // --------------------------------------------------------- selection
  const toggleOne = (key) => setSelected(s => {
    const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n;
  });
  const selectFirstN = (n) => {
    const next = new Set(selected);
    for (const it of sources) { if (next.size >= n) break; next.add(it.key); }
    setSelected(next);
  };
  const clearSelection = () => setSelected(new Set());

  // --------------------------------------------------------- URS bootstrap
  let _uid = 0;
  const proposeOutline = async () => {
    setOutlining(true);
    setError(null);
    setGroups([]);
    setJob(null);
    try {
      const res = await fetch(`/api/build/${setupId}/urs/outline`, {
        method: 'POST', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify({ count: ursCount }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not build an outline');
      const flat = [];
      (d.sections || []).forEach(sec => (sec.items || []).forEach(t =>
        flat.push({ id: `t${_uid++}-${Math.round(performance.now())}-${flat.length}`, title: t, section: sec.title, checked: true })));
      setUrsTitles(flat);
      setOutlined(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setOutlining(false);
    }
  };

  const editTitle = (id, value) => setUrsTitles(ts => ts.map(t => t.id === id ? { ...t, title: value } : t));
  const toggleTitle = (id) => setUrsTitles(ts => ts.map(t => t.id === id ? { ...t, checked: !t.checked } : t));
  const removeTitle = (id) => setUrsTitles(ts => ts.filter(t => t.id !== id));
  const addTitle = (section) => setUrsTitles(ts => [...ts,
    { id: `t${_uid++}-${Math.round(performance.now())}-${ts.length}`, title: '', section, checked: true }]);

  const expandUrs = async () => {
    const titles = ursTitles.filter(t => t.checked && t.title.trim()).map(t => t.title.trim());
    if (!titles.length) return;
    setError(null);
    setGroups([]);
    try {
      const res = await fetch(`/api/build/${setupId}/urs/expand`, {
        method: 'POST', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify({ titles }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not start expansion');
      setJob({ job_id: d.job_id, status: 'processing', progress: 0 });
    } catch (e) {
      setError(e.message);
    }
  };

  // --------------------------------------------------------- generate + poll
  const startGenerate = async () => {
    const chosen = sources.filter(s => selected.has(s.key)).slice(0, BATCH_CAP);
    if (!chosen.length) return;
    setError(null);
    setGroups([]);
    try {
      const res = await fetch(`/api/build/${setupId}/generate`, {
        method: 'POST', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify({ block_id: step.block_id, sources: chosen.map(s => ({ key: s.key, name: s.name })) }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not start generation');
      setJob({ job_id: d.job_id, status: 'processing', progress: 0 });
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    if (!job || job.status !== 'processing') return;
    const poll = async () => {
      try {
        const res = await fetch(`/api/build/jobs/${job.job_id}`, { credentials: 'include' });
        const d = await res.json();
        if (!res.ok) throw new Error('lost the job');
        const result = d.result || {};
        setGroups(prev => (result.groups || []).map(g => {
          const existing = prev.find(p => p.source_key === g.source_key);
          return {
            ...g,
            drafts: g.drafts.map((dr, i) => {
              const id = `${g.source_key}:${i}`;
              const was = existing?.drafts.find(p => p._id === id);
              // Preserve in-progress edits / approval state across polls.
              return was ? { ...dr, ...was } : { ...dr, _id: id, _status: 'draft' };
            }),
          };
        }));
        setJob(j => ({ ...j, status: d.status, progress: d.progress }));
        if (d.status !== 'processing') clearInterval(pollRef.current);
      } catch (e) {
        clearInterval(pollRef.current);
        setJob(j => ({ ...j, status: 'error' }));
        setError('Lost contact with the generation job.');
      }
    };
    pollRef.current = setInterval(poll, 1600);
    poll();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_id]);

  const cancelJob = async () => {
    if (!job) return;
    clearInterval(pollRef.current);
    await fetch(`/api/build/jobs/${job.job_id}`, {
      method: 'DELETE', credentials: 'include', headers: { 'X-CSRF-Token': getCsrfToken() },
    }).catch(() => {});
    setJob(j => j ? { ...j, status: 'cancelled' } : null);
  };

  // --------------------------------------------------------- edit / approve drafts
  const editDraft = (gid, did, field, value) => setGroups(gs => gs.map(g =>
    g.source_key !== gid ? g : { ...g, drafts: g.drafts.map(d => d._id === did
      // Editing the name invalidates a stale duplicate verdict — clear it.
      ? { ...d, [field]: value, ...(field === 'name' ? { _dup: undefined, _dupOverride: false } : {}) }
      : d) }));

  const setDraftStatus = (did, status) => setGroups(gs => gs.map(g =>
    ({ ...g, drafts: g.drafts.map(d => d._id === did ? { ...d, _status: status } : d) })));

  const approveDraft = async (group, draft) => {
    setDraftStatus(draft._id, 'saving');
    try {
      // URS step 0: create-only at L1 (the trace root — no parent, no relation).
      const body = isUrs
        ? { level_num: 1, name: draft.name, description: draft.description || '' }
        : {
            level_num: step.level_num,
            parent_key: group.source_key,
            name: draft.name,
            description: draft.description || '',
            steps: draft.steps || null,
            child_block_id: step.block_id,
          };
      const res = await fetch(`/api/matrix/${setupId}/add-item`, {
        method: 'POST', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok || !(d.status === 'linked' || d.status === 'created' || d.IsSuccess)) {
        throw new Error(d.detail || 'Approve failed');
      }
      // Keep the created item's key + Orcanos link so the card can hyperlink it.
      setGroups(gs => gs.map(g => ({
        ...g, drafts: g.drafts.map(x => x._id === draft._id
          ? { ...x, _status: 'approved', _added: d.item || null } : x),
      })));
    } catch (e) {
      setDraftStatus(draft._id, 'draft');
      setError(e.message);
    }
  };

  const discardDraft = (did) => setDraftStatus(did, 'discarded');

  // --------------------------------------------------------- duplicate check
  const [dupChecking, setDupChecking] = useState(false);
  const [dupRan, setDupRan] = useState(false);

  const runDupCheck = async () => {
    setDupChecking(true);
    setError(null);
    try {
      for (const g of groups) {
        const list = g.drafts.filter(d => d._status === 'draft');
        if (!list.length) continue;
        const res = await fetch(`/api/matrix/${setupId}/ai/dup-check`, {
          method: 'POST', credentials: 'include', headers: jsonHeaders(),
          body: JSON.stringify({
            parent_key: isUrs ? '' : g.source_key,
            child_level_num: isUrs ? 1 : step.level_num,
            child_block_id: isUrs ? null : step.block_id,
            drafts: list.map(d => ({ name: d.name, description: d.description || '' })),
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Duplicate check failed');
        const results = data.results || [];
        setGroups(gs => gs.map(gr => gr.source_key !== g.source_key ? gr : {
          ...gr,
          drafts: gr.drafts.map(d => {
            const k = list.findIndex(x => x._id === d._id);
            return k >= 0 ? { ...d, _dup: results[k] || { status: 'clear', matches: [] } } : d;
          }),
        }));
      }
      setDupRan(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setDupChecking(false);
    }
  };

  const overrideDup = (did) => mapDraftById(did, d => ({ ...d, _dupOverride: true }));

  // Test-step editing on a draft card.
  const mapDraft = (gid, did, fn) => setGroups(gs => gs.map(g =>
    g.source_key !== gid ? g : { ...g, drafts: g.drafts.map(d => d._id === did ? fn(d) : d) }));
  const mapDraftById = (did, fn) => setGroups(gs => gs.map(g =>
    ({ ...g, drafts: g.drafts.map(d => d._id === did ? fn(d) : d) })));
  const editStep = (gid, did, i, field, value) => mapDraft(gid, did, d =>
    ({ ...d, steps: d.steps.map((s, si) => si === i ? { ...s, [field]: value } : s) }));
  const addStep = (gid, did) => mapDraft(gid, did, d =>
    ({ ...d, steps: [...(d.steps || []), { description: '', expected: '' }] }));
  const removeStep = (gid, did, i) => mapDraft(gid, did, d =>
    ({ ...d, steps: d.steps.filter((_, si) => si !== i) }));

  const approveAll = async () => {
    for (const g of groups) {
      for (const d of g.drafts) {
        // Skip a flagged possible-duplicate until the user confirms it with "Add anyway".
        if (d._dup?.status === 'suspect' && !d._dupOverride) continue;
        if (d._status === 'draft') await approveDraft(g, d); // sequential — simple + honest progress
      }
    }
  };

  const applySearch = () => setSearch(searchInput.trim());
  const clearSearch = () => { setSearchInput(''); setSearch(''); };

  // ------------------------------------------------- link mode: suggest + poll
  const startSuggest = async () => {
    const chosen = sources.filter(s => selected.has(s.key)).slice(0, BATCH_CAP);
    if (!chosen.length) return;
    setError(null);
    setLinkGroups([]);
    setLinkMeta(null);
    try {
      const res = await fetch(`/api/build/${setupId}/suggest-links`, {
        method: 'POST', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify({
          block_id: step.block_id, target_scope: tgtFilter,
          sources: chosen.map(s => ({ key: s.key, name: s.name, traced: s.traced_keys || [] })),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not start the suggestion run');
      setLinkJob({ job_id: d.job_id, status: 'processing', progress: 0 });
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    if (!linkJob || linkJob.status !== 'processing') return;
    const poll = async () => {
      try {
        const res = await fetch(`/api/build/jobs/${linkJob.job_id}`, { credentials: 'include' });
        const d = await res.json();
        if (!res.ok) throw new Error('lost the job');
        const result = d.result || {};
        // Preserve per-suggestion state (linked / dismissed) across polls.
        setLinkGroups(prev => (result.groups || []).map(g => {
          const was = prev.find(p => p.source_key === g.source_key);
          return {
            ...g,
            suggestions: (g.suggestions || []).map(s => {
              const old = was?.suggestions.find(p => p.key === s.key);
              return old ? { ...s, ...old } : { ...s, _state: 'open' };
            }),
          };
        }));
        setLinkMeta({
          pool: result.target_pool_size, total: result.target_pool_total,
          truncated: result.target_pool_truncated, aiUsed: result.ai_used !== false,
          hasKnowledge: !!result.has_knowledge,
        });
        setLinkJob(j => ({ ...j, status: d.status, progress: d.progress }));
        if (d.status !== 'processing') clearInterval(linkPollRef.current);
      } catch (e) {
        clearInterval(linkPollRef.current);
        setLinkJob(j => ({ ...j, status: 'error' }));
        setError('Lost contact with the suggestion run.');
      }
    };
    linkPollRef.current = setInterval(poll, 1600);
    poll();
    return () => clearInterval(linkPollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkJob?.job_id]);

  const cancelLinkJob = async () => {
    if (!linkJob) return;
    clearInterval(linkPollRef.current);
    await fetch(`/api/build/jobs/${linkJob.job_id}`, {
      method: 'DELETE', credentials: 'include', headers: { 'X-CSRF-Token': getCsrfToken() },
    }).catch(() => {});
    setLinkJob(j => j ? { ...j, status: 'cancelled' } : null);
  };

  // ------------------------------------------------- link mode: the write
  // Deeper -> shallower, ALWAYS: source = the Ln+1 item (right column), target = the Ln
  // item (left column). add-relation's adjacency guard 400s a flipped pair rather than
  // writing a backwards trace (design §5.1).
  const createTrace = async (targetKey, sourceKey) => {
    const res = await fetch(`/api/matrix/${setupId}/add-relation`, {
      method: 'POST', credentials: 'include', headers: jsonHeaders(),
      body: JSON.stringify({
        source_key: targetKey, source_level: step.level_num,
        target_key: sourceKey, target_level: step.level_num - 1,
      }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.detail || `Link failed (${res.status})`);
    if (d.status !== 'linked') throw new Error(d.error || 'Orcanos refused the trace');
    return d;
  };

  const setSuggestionState = (srcKey, tgtKey, patch) => setLinkGroups(gs => gs.map(g =>
    g.source_key !== srcKey ? g
      : { ...g, suggestions: g.suggestions.map(s => s.key === tgtKey ? { ...s, ...patch } : s) }));

  // Mark the source covered locally so the left column reflects the new trace without a refetch.
  const noteLocalTrace = (srcKey, tgtKey) => setSources(ss => ss.map(s => s.key !== srcKey ? s : {
    ...s, covered: true, traced_count: (s.traced_count || 0) + 1,
    traced_keys: [...(s.traced_keys || []), tgtKey],
  }));

  const linkSuggestion = async (srcKey, s) => {
    setSuggestionState(srcKey, s.key, { _state: 'saving', _err: null });
    try {
      await createTrace(s.key, srcKey);
      setSuggestionState(srcKey, s.key, { _state: 'linked' });
      noteLocalTrace(srcKey, s.key);
    } catch (e) {
      // A stale suggestion (someone else linked it meanwhile) must say so, not vanish.
      setSuggestionState(srcKey, s.key, { _state: 'open', _err: e.message });
    }
  };

  const acceptAllHigh = async () => {
    setLinking(true);
    for (const g of linkGroups) {
      for (const s of g.suggestions) {
        if (s._state === 'open' && s.band === 'high') await linkSuggestion(g.source_key, s);
      }
    }
    setLinking(false);
  };

  const runManualLink = async () => {
    const srcKeys = [...selected], tgtKeys = [...selTargets];
    setConfirm(null);
    setLinking(true);
    let ok = 0, failed = 0;
    for (const sk of srcKeys) {
      for (const tk of tgtKeys) {
        try { await createTrace(tk, sk); noteLocalTrace(sk, tk); ok++; }
        catch (e) { failed++; }
      }
    }
    setLinking(false);
    setSelTargets(new Set());
    setError(failed ? `${ok} trace${ok === 1 ? '' : 's'} created, ${failed} failed. `
      + 'The failed ones may already exist — reload the list to check.' : null);
    loadTargets(true);
  };

  // --------------------------------------------------------- render
  if (loadingSteps) return <div className="tb-page"><div className="tb-loading">Opening the builder…</div></div>;
  if (error && !meta) return (
    <div className="tb-page">
      <div className="tb-error">{error}</div>
      <button className="tb-btn" onClick={() => navigate(backTo)}>← Back to matrix</button>
    </div>
  );

  const selectedCount = selected.size;
  const draftCount = groups.reduce((n, g) => n + g.drafts.filter(d => d._status === 'draft').length, 0);
  const approvedCount = groups.reduce((n, g) => n + g.drafts.filter(d => d._status === 'approved').length, 0);
  const suspectCount = groups.reduce((n, g) => n + g.drafts.filter(d =>
    d._status === 'draft' && d._dup?.status === 'suspect' && !d._dupOverride).length, 0);
  const generating = job?.status === 'processing';
  const suggesting = linkJob?.status === 'processing';
  const showingSuggestions = !!linkJob || linkGroups.length > 0;
  const allSuggestions = linkGroups.flatMap(g => g.suggestions || []);
  const openHigh = allSuggestions.filter(s => s._state === 'open' && s.band === 'high').length;
  const linkedCount = allSuggestions.filter(s => s._state === 'linked').length;
  const bandCount = b => allSuggestions.filter(s => s.band === b).length;
  const manualTraces = selected.size * selTargets.size;

  // URS titles grouped by section (order preserved) + how many are ticked.
  const ursSections = [];
  const ursBySection = {};
  ursTitles.forEach(t => {
    if (!ursBySection[t.section]) { ursBySection[t.section] = []; ursSections.push(t.section); }
    ursBySection[t.section].push(t);
  });
  const ursCheckedCount = ursTitles.filter(t => t.checked && t.title.trim()).length;

  // The target (right) column — shared by the URS bootstrap and every downstream step.
  const renderTarget = ({ headTitle, emptyHint, showSourceHead }) => (
    <div className="tb-col tb-col-target">
      <div className="tb-col-head">{headTitle}</div>
      {generating && (
        <div className="tb-progress">
          <div className="tb-progress-bar"><div style={{ width: `${job.progress || 0}%` }} /></div>
          <div className="tb-progress-row">
            <span>Generating… {job.progress || 0}%</span>
            <button className="tb-btn tb-btn-ghost" onClick={cancelJob}>Cancel</button>
          </div>
        </div>
      )}
      {!job && groups.length === 0 && <div className="tb-empty">{emptyHint}</div>}
      {groups.map(g => (
        <div key={g.source_key} className="tb-group">
          {showSourceHead
            ? <div className="tb-group-head">↳ for <strong>{g.source_key}</strong> <span className="tb-muted">{g.source_name}</span></div>
            : <div className="tb-group-head tb-muted">from “{g.source_name}”</div>}
          {g.error && <div className="tb-group-err">⚠ {g.error}</div>}
          {g.drafts.map(d => {
            const suspect = d._dup?.status === 'suspect' && !d._dupOverride;
            return (
            <div key={d._id} className={`tb-draft tb-draft-${d._status} ${suspect ? 'tb-draft-suspect' : ''}`}>
              {d._status === 'discarded' ? (
                <div className="tb-draft-gone">Discarded · <button className="tb-link" onClick={() => setDraftStatus(d._id, 'draft')}>undo</button></div>
              ) : (
                <>
                  <input className="tb-draft-name" value={d.name} disabled={d._status !== 'draft'}
                    onChange={e => editDraft(g.source_key, d._id, 'name', e.target.value)} />
                  <textarea className="tb-draft-desc" rows={2} value={d.description || ''} disabled={d._status !== 'draft'}
                    onChange={e => editDraft(g.source_key, d._id, 'description', e.target.value)} />
                  {Array.isArray(d.steps) && (d.steps.length > 0 || d._status === 'draft') && (
                    <div className="tb-steps">
                      <div className="tb-steps-label">Test steps</div>
                      {d.steps.length > 0 && (
                        <div className="tb-steps-head"><span>#</span><span>Step description</span><span>Expected result</span>{d._status === 'draft' && <span />}</div>
                      )}
                      {d._status === 'draft' ? (
                        <>
                          {d.steps.map((s, si) => (
                            <div key={si} className="tb-step-row">
                              <span className="tb-step-num">{si + 1}</span>
                              <textarea className="tb-step-in" rows={1} value={s.description}
                                onChange={e => editStep(g.source_key, d._id, si, 'description', e.target.value)} />
                              <textarea className="tb-step-in" rows={1} value={s.expected}
                                onChange={e => editStep(g.source_key, d._id, si, 'expected', e.target.value)} />
                              <button className="tb-step-del" title="Remove step" onClick={() => removeStep(g.source_key, d._id, si)}>×</button>
                            </div>
                          ))}
                          <button className="tb-step-add" onClick={() => addStep(g.source_key, d._id)}>＋ Add step</button>
                        </>
                      ) : (
                        d.steps.map((s, si) => (
                          <div key={si} className="tb-step-row tb-step-ro">
                            <span className="tb-step-num">{si + 1}</span>
                            <div className="tb-step-cell">{s.description}</div>
                            <div className="tb-step-cell">{s.expected}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {suspect && (
                    <div className="tb-dup-warn">
                      <div className="tb-dup-warn-head">⚠ Possible duplicate of an existing item</div>
                      {d._dup.matches.map((m, mi) => (
                        <div key={mi} className="tb-dup-match">
                          {m.url
                            ? <a href={m.url} target="_blank" rel="noopener noreferrer" className="tb-dup-key">{m.key}</a>
                            : <span className="tb-dup-key">{m.key}</span>}
                          <span className="tb-dup-name">{m.name}</span>
                          {m.reason ? <span className="tb-dup-reason">— {m.reason}</span> : null}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="tb-draft-actions">
                    {d._status === 'approved' ? (
                      d._added?.display_key ? (
                        d._added.url
                          ? <a className="tb-added-key" href={d._added.url} target="_blank" rel="noopener noreferrer" title="Open in Orcanos">✓ {d._added.display_key}</a>
                          : <span className="tb-approved">✓ {d._added.display_key}</span>
                      ) : <span className="tb-approved">✓ Added</span>
                    ) : d._status === 'saving' ? <span className="tb-muted">Adding…</span>
                      : suspect ? (<>
                          <button className="tb-btn tb-btn-ghost" onClick={() => discardDraft(d._id)}>Remove</button>
                          <button className="tb-btn tb-btn-ghost tb-dup-anyway" onClick={() => overrideDup(d._id)}>Add anyway</button>
                        </>)
                      : (<>
                          <button className="tb-btn tb-btn-ghost" onClick={() => discardDraft(d._id)}>Discard</button>
                          <button className="tb-btn tb-btn-primary" onClick={() => approveDraft(g, d)}>✓ Approve</button>
                        </>)}
                  </div>
                </>
              )}
            </div>
          );})}
        </div>
      ))}
      {groups.length > 0 && (
        <div className="tb-target-foot">
          <span className="tb-muted">
            {draftCount} draft{draftCount === 1 ? '' : 's'} · {approvedCount} added
            {suspectCount > 0 && <span className="tb-warn"> · {suspectCount} possible duplicate{suspectCount === 1 ? '' : 's'}</span>}
          </span>
          <div className="tb-src-actions">
            {draftCount > 0 && !generating && (
              <button className="tb-btn tb-btn-ghost" onClick={runDupCheck} disabled={dupChecking}>
                {dupChecking ? 'Checking…' : (dupRan ? '🔍 Re-check duplicates' : '🔍 Check for duplicates')}
              </button>
            )}
            {draftCount > 0 && <button className="tb-btn tb-btn-primary" onClick={approveAll}>✓ Approve all</button>}
          </div>
        </div>
      )}
    </div>
  );

  // The right column in "Link existing items" mode: browse the level's real items, or
  // review the AI's proposed traces once a run has produced some.
  // Link state is re-derived on every render from the CURRENT selection, so ticking a source
  // updates the list instantly. The server also filters (so already-linked items don't eat
  // page slots), but re-fetching on every checkbox click would be an Orcanos call per tick.
  const renderLinkTarget = () => {
    const linkedNow = new Set(linkedKeys());
    const visibleTargets = targets.filter(t =>
      !(tgtFilter === 'unlinked' && (t.linked || linkedNow.has(t.key))));
    return (
    <div className="tb-col tb-col-target">
      <div className="tb-col-head">Target — {step.target_label} <span className="tb-muted">(existing items)</span></div>

      {showingSuggestions ? (
        <>
          {suggesting && (
            <div className="tb-progress">
              <div className="tb-progress-bar"><div style={{ width: `${linkJob.progress || 0}%` }} /></div>
              <div className="tb-progress-row">
                <span>Matching… {linkJob.progress || 0}%</span>
                <button className="tb-btn tb-btn-ghost" onClick={cancelLinkJob}>Cancel</button>
              </div>
            </div>
          )}
          {linkMeta && (
            <div className={`tb-link-note ${linkMeta.truncated ? 'warn' : ''}`}>
              {linkMeta.truncated
                ? <>Judged against the first <strong>{linkMeta.pool}</strong> of <strong>{linkMeta.total}</strong> {step.target_label} items —
                   narrow with search to reach the rest.</>
                : <>Judged against all <strong>{linkMeta.pool}</strong> {step.target_label} items.</>}
              {linkMeta.hasKnowledge && <> Product knowledge used for terminology.</>}
              {!linkMeta.aiUsed && <> <strong>No AI available</strong> — these are wording-only matches, so check each one.</>}
            </div>
          )}

          {linkGroups.map(g => (
            <div key={g.source_key} className="tb-group">
              <div className="tb-group-head">↳ for <strong>{g.source_key}</strong> <span className="tb-muted">{g.source_name}</span></div>
              {g.error && <div className="tb-group-err">⚠ {g.error}</div>}
              {!g.error && g.suggestions.length === 0 && (
                <div className="tb-nomatch">— no confident match. Nothing here verifies this {step.source_label}; it likely needs a new {step.target_label}.</div>
              )}
              {g.suggestions.map(s => (
                <div key={s.key} className={`tb-sug tb-sug-${s.band} ${s._state === 'linked' ? 'is-linked' : ''} ${s._state === 'dismissed' ? 'is-gone' : ''}`}>
                  <div className="tb-sug-top">
                    <span className={`tb-conf tb-conf-${s.band}`} title={`Confidence ${s.confidence} — ${s.band} band`}>
                      {s.band === 'high' ? '●●●' : s.band === 'medium' ? '●●○' : '●○○'} {s.confidence.toFixed(2)}
                    </span>
                    <span className="tb-sug-main">
                      {s.url
                        ? <a className="tb-sug-key" href={s.url} target="_blank" rel="noopener noreferrer">{s.key}</a>
                        : <span className="tb-sug-key">{s.key}</span>}
                      <span className="tb-sug-name">{s.name}</span>
                    </span>
                  </div>
                  {s.reason && <div className="tb-sug-why">“{s.reason}”</div>}
                  {s._err && <div className="tb-sug-err">⚠ {s._err}</div>}
                  <div className="tb-sug-acts">
                    {s._state === 'linked' ? <span className="tb-sug-ok">⛓ Traced to {g.source_key}</span>
                      : s._state === 'saving' ? <span className="tb-muted">Linking…</span>
                      : s._state === 'dismissed' ? (
                        <span className="tb-muted">Dismissed · <button className="tb-link" onClick={() => setSuggestionState(g.source_key, s.key, { _state: 'open' })}>undo</button></span>
                      ) : (<>
                        <button className="tb-btn tb-btn-ghost" onClick={() => setSuggestionState(g.source_key, s.key, { _state: 'dismissed' })}>Dismiss</button>
                        <button className="tb-btn tb-btn-primary" disabled={linking} onClick={() => linkSuggestion(g.source_key, s)}>🔗 Link</button>
                      </>)}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {!suggesting && (
            <div className="tb-target-foot">
              <span className="tb-muted">
                {allSuggestions.length} suggestion{allSuggestions.length === 1 ? '' : 's'} ·{' '}
                {bandCount('high')} high · {bandCount('medium')} medium · {bandCount('low')} low
                {linkedCount > 0 && <span className="tb-sug-ok"> · ⛓ {linkedCount} traced</span>}
              </span>
              <div className="tb-src-actions">
                <button className="tb-btn tb-btn-ghost" onClick={() => { setLinkGroups([]); setLinkJob(null); setLinkMeta(null); loadTargets(true); }}>← Back to list</button>
                <button className="tb-btn tb-btn-primary" disabled={!openHigh || linking} onClick={acceptAllHigh}>
                  {linking ? 'Linking…' : `✓ Accept all high (${openHigh})`}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="tb-src-controls">
            <div className="tb-filter" role="group">
              {['unlinked', 'all'].map(f => (
                <button key={f} className={tgtFilter === f ? 'active' : ''} onClick={() => setTgtFilter(f)}>
                  {f === 'unlinked' ? 'Unlinked to selection' : 'All'}
                </button>
              ))}
            </div>
            <div className="tb-search">
              <input value={tgtSearchInput} placeholder="Search…"
                onChange={e => setTgtSearchInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') setTgtSearch(tgtSearchInput.trim());
                  if (e.key === 'Escape') { setTgtSearchInput(''); setTgtSearch(''); }
                }} />
              {tgtSearch ? <button className="tb-search-x" onClick={() => { setTgtSearchInput(''); setTgtSearch(''); }}>×</button> : null}
            </div>
          </div>

          <div className="tb-src-list">
            {visibleTargets.length === 0 && !loadingTgt && (
              <div className="tb-empty-quiet">
                {tgtFilter === 'unlinked'
                  ? `Every ${step.target_label} here is already traced to the selected ${step.source_label} items.`
                  : `This level's filter returns no ${step.target_label} items yet.`}
              </div>
            )}
            {visibleTargets.map(t => {
              const isLinked = t.linked || linkedNow.has(t.key);
              return (
              <label key={t.key} className={`tb-src-item ${selTargets.has(t.key) ? 'sel' : ''} ${isLinked ? 'is-linked' : ''}`}>
                <input type="checkbox" checked={selTargets.has(t.key)} disabled={isLinked}
                  onChange={() => setSelTargets(s => {
                    const n = new Set(s); n.has(t.key) ? n.delete(t.key) : n.add(t.key); return n;
                  })} />
                <span className="tb-src-key">{t.key}</span>
                <span className="tb-src-name">{t.name}</span>
                {isLinked
                  ? <span className="tb-cov tb-cov-yes" title="Already traced to a selected item">⛓ linked</span>
                  : <span className="tb-cov tb-cov-free" title="Not traced to any requirement loaded in this step">○ free</span>}
              </label>
            );})}
            {tgtHasMore && (
              <button className="tb-loadmore" onClick={() => loadTargets(false)} disabled={loadingTgt}>
                {loadingTgt ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>

          <div className="tb-src-foot">
            <div className="tb-src-stat">
              {selTargets.size} selected · {visibleTargets.length} shown of {tgtTotal}
            </div>
            <div className="tb-src-actions">
              {selTargets.size > 0 && <button className="tb-btn tb-btn-ghost" onClick={() => setSelTargets(new Set())}>Clear</button>}
              <button className="tb-btn tb-btn-primary" disabled={!manualTraces || linking}
                onClick={() => setConfirm({ srcKeys: [...selected], tgtKeys: [...selTargets] })}>
                {linking ? 'Linking…' : `🔗 Link ${selected.size} → ${selTargets.size} (${manualTraces} trace${manualTraces === 1 ? '' : 's'})`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
    );
  };

  return (
    <div className="tb-page">
      <div className="tb-header">
        <button className="tb-back" onClick={() => navigate(`/trace/matrix/${setupId}`)}>← Matrix</button>
        <h1 className="tb-title">✨ Trace Builder — {meta.panel_name}</h1>
        <button className="tb-btn" onClick={() => navigate(`/trace/build/${setupId}/import`)}
          title="Take requirements, tests and traces out of the customer's documents">📄 Import from documents</button>
        {meta.product && (
          <span className={`tb-knowledge ${meta.product.has_doc ? 'ok' : 'none'}`}
            title={meta.product.has_doc ? 'Product knowledge will be used as context' : 'No knowledge document — generation runs without product context'}>
            <KnowledgeIcon size={15} /> {meta.product.name}{meta.product.has_doc ? ' ✓' : ' — no doc'}
          </span>
        )}
      </div>

      {/* Step rail */}
      <div className="tb-rail">
        <div className="tb-rail-node tb-rail-knowledge">① Knowledge</div>
        <div className="tb-rail-arrow">▸</div>
        <button className={`tb-rail-node tb-rail-base tb-rail-step ${isUrs ? 'active' : ''}`}
          onClick={() => setSel({ type: 'urs' })} title="Populate the root requirements from the knowledge document">
          <strong>{meta.base.label || 'Requirements'}</strong>
        </button>
        {meta.steps.map((s, i) => (
          <React.Fragment key={s.block_id}>
            <div className="tb-rail-arrow">▸</div>
            <button className={`tb-rail-node tb-rail-step ${sel.type === 'step' && sel.idx === i ? 'active' : ''}`}
              onClick={() => setSel({ type: 'step', idx: i })}>
              {s.source_label} → <strong>{s.target_label}</strong>
            </button>
          </React.Fragment>
        ))}
      </div>

      {isUrs ? (
        <div className="tb-columns">
          {/* URS OUTLINE */}
          <div className="tb-col tb-col-source">
            <div className="tb-col-head">Outline — {meta.base.label || 'Requirements'}</div>
            {!meta.product?.has_doc ? (
              <div className="tb-empty">
                This panel's product has no knowledge document yet. Add sources and generate the
                document in <KnowledgeIcon size={14} /> Product Knowledge first — the outline is built from it.
              </div>
            ) : (
              <>
                <div className="tb-urs-controls">
                  <label className="tb-urs-count-label">How many?
                    <input type="number" min="1" max="100" className="tb-urs-count" value={ursCount}
                      onChange={e => setUrsCount(Math.max(1, Math.min(100, parseInt(e.target.value || '0', 10) || 1)))} />
                  </label>
                  <button className="tb-btn tb-btn-primary" onClick={proposeOutline} disabled={outlining}>
                    {outlining ? 'Thinking…' : (outlined ? '↻ Re-propose' : '✨ Propose outline')}
                  </button>
                </div>
                {!outlined && !outlining && (
                  <div className="tb-empty">
                    Propose an outline of requirement titles from the product knowledge. Edit the
                    list — rename, delete, add — then expand the ones you keep into full requirements.
                  </div>
                )}
                {ursTitles.length > 0 && (
                  <>
                    <div className="tb-src-list tb-urs-list">
                      {ursSections.map(secName => (
                        <div key={secName} className="tb-urs-section">
                          <div className="tb-urs-section-head">{secName}
                            <button className="tb-urs-add" title="Add a requirement here" onClick={() => addTitle(secName)}>＋</button>
                          </div>
                          {ursBySection[secName].map(t => (
                            <div key={t.id} className={`tb-urs-title ${t.checked ? '' : 'off'}`}>
                              <input type="checkbox" checked={t.checked} onChange={() => toggleTitle(t.id)} />
                              <input className="tb-urs-title-input" value={t.title} placeholder="Requirement title…"
                                onChange={e => editTitle(t.id, e.target.value)} />
                              <button className="tb-urs-del" title="Remove" onClick={() => removeTitle(t.id)}>×</button>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    <div className="tb-src-foot">
                      <div className="tb-src-stat">{ursCheckedCount} of {ursTitles.length} selected</div>
                      <div className="tb-src-actions">
                        <button className="tb-btn tb-btn-primary" onClick={expandUrs} disabled={!ursCheckedCount || generating}>
                          ✨ Expand {ursCheckedCount}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          {renderTarget({
            headTitle: meta.base.label || 'Requirements',
            emptyHint: 'Expand the selected titles to draft full requirements here, then approve them into the project.',
            showSourceHead: false,
          })}
        </div>
      ) : meta.steps.length === 0 ? (
        <div className="tb-empty">This panel has no authorable levels below the root. Add a level to the panel first.</div>
      ) : (
        <>
        {/* Mode switch — create NEW children, or link to items that already exist. */}
        <div className="tb-mode-row">
          <div className="tb-mode-switch" role="group" aria-label="Builder mode">
            <button className={mode === 'create' ? 'active' : ''} onClick={() => setMode('create')}>
              Create new items
            </button>
            <button className={mode === 'link' ? 'active' : ''} onClick={() => setMode('link')}>
              Link existing items
            </button>
          </div>
          <span className="tb-mode-hint">
            {mode === 'create'
              ? `Draft new ${step.target_label} items from each ${step.source_label}.`
              : `Trace each ${step.source_label} to ${step.target_label} items that already exist.`}
          </span>
        </div>

        <div className="tb-columns">
          {/* SOURCE */}
          <div className="tb-col tb-col-source">
            <div className="tb-col-head">Source — {step.source_label}</div>
            <div className="tb-src-controls">
              <div className="tb-filter" role="group">
                {['uncovered', 'all', 'covered'].map(f => (
                  <button key={f} className={srcFilter === f ? 'active' : ''} onClick={() => setSrcFilter(f)}>
                    {f === 'uncovered' ? 'Not covered' : f === 'covered' ? 'Covered' : 'All'}
                  </button>
                ))}
              </div>
              <div className="tb-search">
                <input value={searchInput} placeholder="Search…"
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') applySearch(); if (e.key === 'Escape') clearSearch(); }} />
                {search ? <button className="tb-search-x" onClick={clearSearch}>×</button> : null}
              </div>
            </div>

            <div className="tb-src-list">
              {sources.length === 0 && !loadingSrc && (
                <div className="tb-empty-quiet">
                  {srcFilter === 'uncovered' ? 'No uncovered items here — every source has a ' + step.target_label + '.' : 'No items.'}
                </div>
              )}
              {sources.map(s => (
                <label key={s.key} className={`tb-src-item ${selected.has(s.key) ? 'sel' : ''}`}>
                  <input type="checkbox" checked={selected.has(s.key)} onChange={() => toggleOne(s.key)} />
                  <span className="tb-src-key">{s.key}</span>
                  <span className="tb-src-name">{s.name}</span>
                  {s.covered
                    ? <span className="tb-cov tb-cov-yes" title={`${s.traced_count} ${step.target_label}`}>✓ covered</span>
                    : <span className="tb-cov tb-cov-no">⚠ not covered</span>}
                </label>
              ))}
              {hasMore && (
                <button className="tb-loadmore" onClick={() => loadSources(false)} disabled={loadingSrc}>
                  {loadingSrc ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>

            <div className="tb-src-foot">
              <div className="tb-src-stat">
                {selectedCount} selected · {sources.length} loaded{hasMore ? '+' : ''} of {sourceTotal}
                {selectedCount > BATCH_CAP && <span className="tb-warn"> · cap {BATCH_CAP}</span>}
              </div>
              <div className="tb-src-actions">
                <button className="tb-btn tb-btn-ghost" onClick={() => selectFirstN(50)}>Select first 50</button>
                {selectedCount > 0 && <button className="tb-btn tb-btn-ghost" onClick={clearSelection}>Clear</button>}
                {mode === 'link' ? (
                  <button className="tb-btn tb-btn-primary" onClick={startSuggest}
                    disabled={!selectedCount || suggesting}>
                    ✨ Suggest links for {Math.min(selectedCount, BATCH_CAP)}
                  </button>
                ) : (
                  <button className="tb-btn tb-btn-primary" onClick={startGenerate} disabled={!selectedCount || generating}>
                    ✨ Generate for {Math.min(selectedCount, BATCH_CAP)}
                  </button>
                )}
              </div>
            </div>
          </div>

          {mode === 'link' ? renderLinkTarget() : renderTarget({
            headTitle: <>Target — {step.target_label}{step.is_test && <span className="tb-muted"> (test cases)</span>}</>,
            emptyHint: 'Select source items on the left and click ✨ Generate. Drafts appear here for review.',
            showSourceHead: true,
          })}
        </div>
        </>
      )}

      {confirm && (
        <div className="tb-scrim" onClick={e => { if (e.target === e.currentTarget) setConfirm(null); }}>
          <div className="tb-dialog" role="dialog" aria-modal="true">
            <h3>Create traces?</h3>
            <p>
              This creates <strong>{confirm.srcKeys.length * confirm.tgtKeys.length} trace
              {confirm.srcKeys.length * confirm.tgtKeys.length === 1 ? '' : 's'}</strong> — each of the{' '}
              <strong>{confirm.tgtKeys.length}</strong> selected {step.target_label} item
              {confirm.tgtKeys.length === 1 ? '' : 's'} will be traced to each of the{' '}
              <strong>{confirm.srcKeys.length}</strong> selected {step.source_label} item
              {confirm.srcKeys.length === 1 ? '' : 's'}.
            </p>
            <div className="tb-dialog-acts">
              <button className="tb-btn tb-btn-ghost" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="tb-btn tb-btn-primary" onClick={runManualLink}>Create traces</button>
            </div>
          </div>
        </div>
      )}

      {error && meta && <div className="tb-error tb-error-float">{error} <button className="tb-link" onClick={() => setError(null)}>dismiss</button></div>}

      {engine && (
        <div className="tb-footer">
          <span className="tb-footer-dot" />
          AI engine: <strong>{engine.provider}</strong>
          <code className="tb-footer-model">{engine.model}</code>
        </div>
      )}
    </div>
  );
}
